// ---------------------------------------------------------------------------
// Cast / Remote-play channel (frontend-only, zero backend, zero deps).
//
// How it works
//  - TV side:   startCastReceiver() -> connects to a public MQTT broker over
//               WebSocket, subscribes to flexwatch/tv/<CODE> and shows the
//               6-digit CODE on screen.
//  - Phone side: sendToTv(code, command) -> publishes play/stop to
//               flexwatch/tv/<CODE>. TV navigates to the player instantly.
//
// Brokers: free public MQTT brokers with WSS support (no account needed).
//
// Transport: a MINIMAL MQTT 3.1.1 client implemented below (only CONNECT /
// CONNACK / SUBSCRIBE / SUBACK / PUBLISH qos0 / PINGREQ / PINGRESP). Written
// from scratch because the `mqtt` npm package needs Node core polyfills that
// webpack 5 (CRA) does not ship, and it must parse on old Android 9 TV
// WebViews. Pure ES5-style code, no optional chaining, no classes.
// ---------------------------------------------------------------------------

import { generatePairCode } from './capability';

// Secure (wss) and plain (ws) broker lists — ports DIFFER per scheme.
const BROKERS_WSS = [
  'wss://broker.emqx.io:8084/mqtt',
  'wss://test.mosquitto.org:8081/mqtt',
];
const BROKERS_WS = [
  'ws://broker.emqx.io:8083/mqtt',
  'ws://test.mosquitto.org:8080/mqtt',
];

const TOPIC_PREFIX = 'flexwatch/tv/';
const KEEPALIVE_SECONDS = 60;

const nextBroker = () => {
  try {
    // Inside the TV APK (file://) or http hosting, plain ws has less overhead.
    const isHttps = typeof window !== 'undefined' && window.location &&
      /^https:/i.test(window.location.protocol || '');
    if (!isHttps) return BROKERS_WS;
  } catch (e) { /* fall through */ }
  return BROKERS_WSS;
};

// ---------------------------------------------------------------------------
// UTF-8 helpers (no TextEncoder/TextDecoder dependency — works on any WebView)
// ---------------------------------------------------------------------------
const utf8Bytes = (str) => {
  const s = encodeURIComponent(String(str));
  const out = [];
  for (let i = 0; i < s.length; i++) {
    if (s.charAt(i) === '%') {
      out.push(parseInt(s.substr(i + 1, 2), 16));
      i += 2;
    } else {
      out.push(s.charCodeAt(i));
    }
  }
  return out;
};

const utf8Str = (bytes) => {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  try {
    return decodeURIComponent(escape(s));
  } catch (e) {
    return s;
  }
};

// ---------------------------------------------------------------------------
// MQTT packet builders (3.1.1)
// ---------------------------------------------------------------------------
const remLen = (n) => {
  const out = [];
  let v = n;
  do {
    let d = v % 128;
    v = Math.floor(v / 128);
    if (v > 0) d |= 128;
    out.push(d);
  } while (v > 0);
  return out;
};

const encStr = (s) => {
  const b = utf8Bytes(s);
  return [(b.length >> 8) & 255, b.length & 255].concat(b);
};

const buildConnect = (clientId) => {
  // protocol name "MQTT", level 4 (3.1.1), clean-session flag, keepalive
  const vh = [0x00, 0x04, 0x4d, 0x51, 0x54, 0x54, 0x04, 0x02,
    (KEEPALIVE_SECONDS >> 8) & 255, KEEPALIVE_SECONDS & 255];
  const body = vh.concat(encStr(clientId));
  return [0x10].concat(remLen(body.length)).concat(body);
};

let packetId = 1;
const buildSubscribe = (topic) => {
  const pid = packetId++ % 65536 || 1;
  const body = [(pid >> 8) & 255, pid & 255].concat(encStr(topic)).concat([0x00]);
  return [0x82].concat(remLen(body.length)).concat(body);
};

const buildPublish = (topic, payloadStr) => {
  const body = encStr(topic).concat(utf8Bytes(payloadStr));
  return [0x30].concat(remLen(body.length)).concat(body);
};

const PINGREQ = [0xc0, 0x00];

// ---------------------------------------------------------------------------
// Minimal client: one WebSocket session on a single URL.
// handlers: { onConnect(api), onMessage(msg), onDown(err) }
// api: { publish(topic, str), subscribe(topic), end() }
// ---------------------------------------------------------------------------
const createMqttClient = (url, handlers) => {
  let ws = null;
  let opened = false;
  let dead = false;
  let pingTimer = null;
  let connTimer = null;
  let buffer = [];

  const sendBytes = (bytes) => {
    try {
      if (ws && ws.readyState === 1) ws.send(new Uint8Array(bytes));
    } catch (e) { /* socket gone */ }
  };

  const handlePacket = (byte1, body) => {
    const type = byte1 & 0xf0;
    if (type === 0x20) { // CONNACK
      const rc = body.length > 1 ? body[1] : 5;
      if (rc === 0) {
        opened = true;
        pingTimer = setInterval(() => sendBytes(PINGREQ), (KEEPALIVE_SECONDS - 15) * 1000);
        handlers.onConnect(api);
      } else {
        onDown(new Error('broker refused connection (rc=' + rc + ')'));
      }
    } else if (type === 0x30) { // PUBLISH (qos0 expected; skip packet id if qos>0)
      const qos = (byte1 >> 1) & 0x03;
      if (body.length < 2) return;
      const tlen = (body[0] << 8) | body[1];
      let start = 2 + tlen;
      if (qos > 0) start += 2;
      if (body.length < start) return;
      const payload = body.slice(start);
      let msg = null;
      try { msg = JSON.parse(utf8Str(payload)); } catch (e) { msg = null; }
      if (msg && msg.type && handlers.onMessage) handlers.onMessage(msg);
    }
    // SUBACK (0x90) / PINGRESP (0xd0) — nothing to do
  };

  const onBytes = (arr) => {
    buffer = buffer.concat(arr);
    for (;;) {
      if (buffer.length < 2) return;
      const byte1 = buffer[0];
      let len = 0;
      let mul = 1;
      let i = 1;
      let complete = false;
      for (; i < buffer.length && i <= 4; i++) {
        const b = buffer[i];
        len += (b & 127) * mul;
        mul *= 128;
        if (!(b & 128)) { complete = true; break; }
      }
      if (!complete) return; // wait for more length bytes
      const total = i + 1 + len;
      if (buffer.length < total) return; // wait for the full body
      const body = buffer.slice(i + 1, total);
      buffer = buffer.slice(total);
      handlePacket(byte1, body);
    }
  };

  const onDown = (err) => {
    if (dead) return;
    dead = true;
    clearInterval(pingTimer);
    clearTimeout(connTimer);
    try {
      if (ws) {
        ws.onopen = ws.onclose = ws.onerror = ws.onmessage = null;
        try { ws.close(); } catch (e) { /* noop */ }
      }
    } catch (e) { /* noop */ }
    ws = null;
    handlers.onDown(err || new Error(opened ? 'closed' : 'connect failed'));
  };

  try {
    ws = new WebSocket(url, 'mqtt');
  } catch (e) {
    onDown(e);
    return null;
  }
  ws.binaryType = 'arraybuffer';

  connTimer = setTimeout(() => onDown(new Error('connect timeout')), 8000);

  ws.onopen = () => sendBytes(buildConnect('fw_' + Math.random().toString(16).slice(2, 10) + Date.now().toString(36)));

  ws.onmessage = (event) => {
    try {
      const data = event.data;
      if (typeof data === 'string') return; // brokers send binary MQTT frames
      const buf = data instanceof ArrayBuffer ? new Uint8Array(data) : data;
      onBytes(Array.prototype.slice.call(buf));
    } catch (e) { /* malformed frame — ignore */ }
  };

  ws.onerror = () => onDown(new Error('socket error'));
  ws.onclose = () => onDown(new Error('socket closed'));

  const api = {
    subscribe: (topic) => sendBytes(buildSubscribe(topic)),
    publish: (topic, payloadStr) => sendBytes(buildPublish(topic, payloadStr)),
    end: () => {
      dead = true;
      clearInterval(pingTimer);
      clearTimeout(connTimer);
      try { if (ws) { ws.onopen = ws.onclose = ws.onerror = ws.onmessage = null; ws.close(); } } catch (e) { /* noop */ }
      ws = null;
    },
  };

  return api;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

// TV side: listen for cast commands on flexwatch/tv/<code>.
// Returns { code, stop() }.
//   onCommand(msg) -> parsed JSON for play/stop/pause/resume/ping
//   onStatus('connecting'|'online'|'offline')
export const startCastReceiver = (onCommand, onStatus) => {
  const code = generatePairCode();
  const topic = TOPIC_PREFIX + code;
  const brokers = nextBroker();
  let stopped = false;
  let current = null;
  let attempt = 0;

  const status = (s) => { try { if (onStatus) onStatus(s); } catch (e) { /* noop */ } };
  status('connecting');

  const open = () => {
    if (stopped) return;
    const url = brokers[attempt % brokers.length];
    current = createMqttClient(url, {
      onConnect: (c) => {
        attempt = 0;
        c.subscribe(topic);
        status('online');
      },
      onMessage: (msg) => {
        if (stopped) return;
        if (msg.type === 'ping') {
          // answer the phone's probe on a separate reply topic
          try {
            current.publish(TOPIC_PREFIX + 'probe/' + code, JSON.stringify({ type: 'pong', ts: Date.now() }));
          } catch (e) { /* noop */ }
          return;
        }
        try { onCommand(msg); } catch (e) { /* noop */ }
      },
      onDown: () => {
        if (stopped) return;
        status('offline');
        attempt += 1;
        const delay = Math.min(30000, 2000 * attempt);
        setTimeout(open, delay);
      },
    });
  };

  open();

  return {
    code: code,
    // TV side: publish live playback state to the remote's state topic.
    publishState: (extra) => {
      try {
        if (current) {
          current.publish(TOPIC_PREFIX + 'state/' + code,
            JSON.stringify(Object.assign({ type: 'state', ts: Date.now() }, extra || {})));
        }
      } catch (e) { /* noop */ }
    },
    stop: () => {
      stopped = true;
      try { if (current) current.end(); } catch (e) { /* noop */ }
    },
  };
};

// Phone side: publish one command to a TV. Resolves { ok, reason? }.
// Commands: { type:'play', path:'/watch/movie/123', title, poster_path, ... }
//           { type:'stop' }  { type:'pause' }  { type:'resume' }
export const sendToTv = (code, command) =>
  new Promise((resolve) => {
    const clean = String(code || '').replace(/\D/g, '');
    if (clean.length !== 6) { resolve({ ok: false, reason: 'invalid-code' }); return; }

    const topic = TOPIC_PREFIX + clean;
    const msg = JSON.stringify(Object.assign({ type: 'play' }, command || {}, { ts: Date.now() }));
    const brokers = nextBroker();

    let done = false;
    let current = null;
    let idx = 0;
    let timer = null;

    const settle = (result) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      try { if (current) current.end(); } catch (e) { /* noop */ }
      resolve(result);
    };

    const tryBroker = () => {
      if (done) return;
      if (idx >= brokers.length) { settle({ ok: false, reason: 'connect-failed' }); return; }
      const url = brokers[idx++];
      current = createMqttClient(url, {
        onConnect: (c) => {
          c.publish(topic, msg);
          timer = setTimeout(() => settle({ ok: true }), 500); // qos0 — consider delivered
        },
        onMessage: () => { /* sender ignores */ },
        onDown: () => tryBroker(),
      });
      if (!current) tryBroker();
    };

    tryBroker();
  });

// Phone side: open a live command channel to a TV and keep it open.
// Returns a handle { send(cmd), probe(), stop() } so a remote UI can fire many
// commands (play/pause/seek/...) over ONE MQTT session instead of reconnecting
// per button press (each sendToTv() costs a full TCP+MQTT handshake).
//
// Commands accepted by the TV side (receiver.html + WatchPlayer hooks):
//   play path title | stop | pause | resume | seek <sec> | seekBy <±sec>
//   vol <0-100> | mute | unmute | episode <s> <e> | source <n> | server <n>
//   quality:hd|sd|auto | audio <lang> | subtitle <lang>|off | settings
//   hd | nextSource | audioTrack <n> | subtitleTrack <n>|off | subtitleOff
export const openRemoteChannel = (code) => {
  const clean = String(code || '').replace(/\D/g, '');
  if (clean.length !== 6) return null;

  const topic = TOPIC_PREFIX + clean;
  const brokers = nextBroker();
  let current = null;
  let idx = 0;
  let stopped = false;
  const pending = [];

  const flush = () => {
    while (pending.length && current) {
      try { current.publish(topic, JSON.stringify(Object.assign({ ts: Date.now() }, pending.shift()))); }
      catch (e) { break; }
    }
  };

  const tryBroker = () => {
    if (stopped) return;
    // Sab brokers fail ho chuke to idx ko 0 par reset karo. Warna `idx` stale
    // rehta aur har baar `idx >= brokers.length` branch chalta rehta — channel
    // kabhi dobara connect hi nahi hota tha (mobile remote silently dead).
    if (idx >= brokers.length) { idx = 0; setTimeout(tryBroker, 3000); return; }
    const url = brokers[idx++];
    current = createMqttClient(url, {
      onConnect: (c) => { idx = 0; flush(); },
      onMessage: () => { /* remote is send-only */ },
      onDown: () => { current = null; tryBroker(); },
    });
    if (!current) { setTimeout(tryBroker, 1000); }
  };

  tryBroker();

  return {
    send: (command) => {
      if (stopped) return;
      pending.push(Object.assign({ type: String((command && command.type) || 'ping') }, command));
      flush();
    },
    stop: () => {
      stopped = true;
      pending.length = 0;
      try { if (current) current.end(); } catch (e) { /* noop */ }
      current = null;
    },
  };
};

// Phone side: subscribe to the TV's live state topic (paused, position, title).
// TV publishes state after every command; the remote UI renders it in real time.
// Returns { getState(), stop() }.
export const subscribeTvState = (code, onState) => {
  const clean = String(code || '').replace(/\D/g, '');
  if (clean.length !== 6) return { getState: () => null, stop: () => {} };

  const stateTopic = TOPIC_PREFIX + 'state/' + clean;
  const brokers = nextBroker();
  let current = null;
  let idx = 0;
  let latest = null;
  let stopped = false;

  const tryBroker = () => {
    if (stopped) return;
    // Same stale-idx trap as openRemoteChannel — reset so the state feed
    // reconnects after every broker has failed once.
    if (idx >= brokers.length) { idx = 0; setTimeout(tryBroker, 3000); return; }
    const url = brokers[idx++];
    current = createMqttClient(url, {
      onConnect: (c) => {
        idx = 0;
        c.subscribe(stateTopic); // live state from the TV
      },
      onMessage: (msg) => {
        if (stopped) return;
        if (msg.type === 'state') { latest = msg; try { if (onState) onState(msg); } catch (e) { /* noop */ } }
      },
      onDown: () => { current = null; tryBroker(); },
    });
    if (!current) { setTimeout(tryBroker, 1000); }
  };

  tryBroker();

  return {
    getState: () => latest,
    stop: () => {
      stopped = true;
      try { if (current) current.end(); } catch (e) { /* noop */ }
      current = null;
    },
  };
};

// Phone side: check whether a TV is listening on <code>. Resolves true/false.
export const probeTv = (code) =>
  new Promise((resolve) => {
    const clean = String(code || '').replace(/\D/g, '');
    if (clean.length !== 6) { resolve(false); return; }

    const topic = TOPIC_PREFIX + clean;
    const replyTopic = TOPIC_PREFIX + 'probe/' + clean;
    const brokers = nextBroker();

    let done = false;
    let current = null;
    let idx = 0;
    let waitTimer = null;

    const settle = (v) => {
      if (done) return;
      done = true;
      clearTimeout(waitTimer);
      try { if (current) current.end(); } catch (e) { /* noop */ }
      resolve(v);
    };

    const tryBroker = () => {
      if (done) return;
      if (idx >= brokers.length) { settle(false); return; }
      const url = brokers[idx++];
      current = createMqttClient(url, {
        onConnect: (c) => {
          c.subscribe(replyTopic);
          c.publish(topic, JSON.stringify({ type: 'ping', ts: Date.now() }));
          waitTimer = setTimeout(() => settle(false), 4000);
        },
        onMessage: (msg) => {
          if (msg.type === 'pong') settle(true);
        },
        onDown: () => tryBroker(),
      });
      if (!current) tryBroker();
    };

    tryBroker();
  });

export default { startCastReceiver, sendToTv, probeTv, openRemoteChannel, subscribeTvState };
